import { useForm, ValidateInput, LoadingComponent, LoadingButton } from "usbc-web-react-library";
import { formatPhoneNumber, formatDate } from "../../shared/helpers";
import { useEffect, useState, useRef } from "react";
import useManageApi from "../../../hooks/useManageApi";
import ValidationErrors from "../../shared/ValidationErrors";
import './MinorAthleteTraining.css'
import { info_icon } from "../../shared/iconSvgs";

const requiredFieldMsg = (fieldName) => `Please enter ${fieldName}`;
const requiredSelectMsg = (fieldName) => `Please select ${fieldName}`;
const invalidFieldMsg = (fieldName) => `${fieldName} is invalid`;

const MinorAthleteTraining = () => {
    const { getMinorAthleteTraining, loading: getMinorAthleteTrainingLoading } = useManageApi();
    const { submitMinorAthleteTraining, loading: submitMinorAthleteTrainingLoading } = useManageApi();
    const [minorAthleteTrainingViewModel, setMinorAthleteTrainingViewModel] = useState(null);
    const [validationErrors, setValidationErrors] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0); //Tab and Enter Key Behavior
    const [formFieldElements, setFormFieldElements] = useState([]); //Tab and Enter Key Behavior
    const [formRefState, setFormRefState] = useState(null); //Tab and Enter Key Behavior
    const formRef = useRef(null); // Tab and Enter Key Behavior

    const {
        handleChange,
        handleSubmit,
        setValues,
        updateError,
        values,
        errors
    } = useForm(submitFormCallback, validate);

    useEffect(() => {
        getMinorAthleteTraining({
            onError: (error) =>{
                setValidationErrors(error);
                window.scrollTo(0, 0);
            }
        }).then((result) => {
            setMinorAthleteTrainingViewModel(result?.data);
        });
    }, []);

    useEffect(() => {
        if (!minorAthleteTrainingViewModel) {
            return;
        }
        setValues({
            ...values,
            minorName: minorAthleteTrainingViewModel.minorName,
            minorNationalId: minorAthleteTrainingViewModel.minorNationalId,
            dateOfSignature: minorAthleteTrainingViewModel.dateOfSignature
        });
    }, [minorAthleteTrainingViewModel]);

    function validate(name, value, displayName) {
        let testValue = name != "postalCode" ? value.trim() : value;
        switch (name) {
            case 'minorNationalId':
                if (value.indexOf('-') !== -1 && value.split('-')[0].length > 5) {
                    updateError(name, "Prefix cannot be longer than 5 digits.");
                    break;
                }
                if (value.indexOf('-') !== -1 && value.split('-')[1].length > 7) {
                    updateError(name, "Suffix cannot be longer than 7 digits.");
                    break;
                }
                if (value.indexOf('-') !== -1 && !(/^[0-9]+(-[0-9]+)+$/.test(value))) {
                    updateError(name, "Must be valid Membership ID in the correct format.");
                    break;
                }

                if (value === null || value === "") {
                    updateError(name, requiredFieldMsg("Member ID."));
                } else {
                    updateError(name);
                }
                break;
            case 'minorName':
                if (value === null || value === "") {
                    updateError(name, requiredFieldMsg(displayName));
                } else {
                    updateError(name);
                }
                break;
            case 'parentName':
                if (value === null || value === "") {
                    updateError(name, requiredFieldMsg(displayName));
                } else {
                    updateError(name);
                }
                break;
            case 'parentEmail':
                if (!new RegExp("^\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*$").test(testValue)) {
                    updateError(name, 'Please enter valid e-mail address.');
                    break;
                }
                if (value === null || value === "") {
                    updateError(name, requiredFieldMsg(displayName));
                } else {
                    updateError(name);
                }
                value = testValue; // Use trimmed value
                break;
            case 'parentSignature':
                if (value === null || value === "") {
                    updateError(name, requiredFieldMsg(displayName));
                } else {
                    updateError(name);
                }
                break;
            case 'parentPhone':
                if (testValue === null || testValue === "") {
                    updateError(name, requiredFieldMsg(displayName));
                }
                else {
                    testValue = formatPhoneNumber(testValue, values?.country !== null ? values?.country : "")?.replace(/( |-)+$/g, '');
                    if ((values?.country === "US" || values?.country === "CA") && !new RegExp(/(\d{3})\)s* \d{3}-\d{4}( X\d{0,})?/gm).test(testValue)) {
                        updateError(name, 'Please enter a valid phone number in (999) 999 9999 x9999 format');
                    }
                    else {
                        updateError(name);
                    }
                    value = testValue;
                }
                break;
            case 'dateOfSignature':
                if (value === null || value === "") {
                    updateError(name, requiredSelectMsg(displayName));
                } else {
                    updateError(name);
                }
                break;
            default:
                break;
        }
        return value;
    }

    function submitFormCallback() {
        setValidationErrors(null);
        //Minor information is discarded
        let modifiedData = { ...minorAthleteTrainingViewModel };
        modifiedData.parentName = values?.parentName;
        modifiedData.parentEmail = values?.parentEmail;
        modifiedData.parentSignature = values?.parentSignature;
        modifiedData.parentPhone = values?.parentPhone;
        modifiedData.dateOfSignature = values?.dateOfSignature;
        submitMinorAthleteTraining({
            body: modifiedData,
            onError: (errors) => {
                setValidationErrors(errors);
                // Scroll to the top of the page
                window.scrollTo(0, 0);
            }
        }).then((result) => {
            if (result && result.data) {
                window.location.href = result.data;
            }
        });
    }
    // ========= START: Custom Tab and Entery Key form behavior. =========

    useEffect(() =>{
        setCurrentIndex(0);
    },[getMinorAthleteTrainingLoading]);

    useEffect(() => {
        if(formRef.current){
            reevaluateFormElements(formRef.current);
        }
    }, [formRefState]);

    function handleFormRefChange(ref) {
        setFormRefState(ref);
        formRef.current = ref;
    }

    useEffect(() => {
        const handleFocusChange = (e) => {
            // Find the index of the focused element within the form elements array
            const index = formFieldElements.findIndex(element => element === e.target);
            // Update the current index when a focusable input is focused
            setCurrentIndex(index);
        };

        formFieldElements.forEach(element => {
            element.addEventListener('focus', handleFocusChange);
        });

        return () => {
            formFieldElements.forEach(element => {
                element.removeEventListener('focus', handleFocusChange);
            });
        };
    }, [formFieldElements]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            // Check if the focused element has the 'profile-form-loading-button' class
            if (formFieldElements[currentIndex].classList.contains('minor-form-loading-button') && e.key === 'Enter') {
                // Allow Enter key's default behavior for elements with the class
                return;
            }
            e.preventDefault();
            const newIndex = e.shiftKey
                ? (currentIndex - 1 + formFieldElements.length) % formFieldElements.length
                : (currentIndex + 1) % formFieldElements.length;
            setCurrentIndex(newIndex);

            // Focus on the new element
            formFieldElements[newIndex].focus();
        }
    };

    // Function to reevaluate form elements when the form changes
    function reevaluateFormElements(form) {
        // Collect all focusable elements within the form, excluding deactivated elements
        const formElements = form.querySelectorAll(
            'input:not([disabled]), button:not([disabled]):not([tabindex="-1"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        // Convert NodeList to array for easier manipulation
        const formElementsArray = Array.from(formElements);

        if (formElementsArray.length > 0) {
            formElementsArray[0].focus();
        }

        setFormFieldElements(formElementsArray);
    }
    // End: ========= Custom Tab and Entery Key form behavior. =========


    return (
        <>
            <LoadingComponent isLoading={() => getMinorAthleteTrainingLoading} description={"Loading Minor Athlete Training... Please Wait."}>
                <h4><strong>Authorization for Minor Participation in Abuse Prevention Training</strong></h4>
                {validationErrors && <ValidationErrors className="validation-errors alert alert-danger mt-3" validationErrors={validationErrors} />}
                {minorAthleteTrainingViewModel?.isError === true && !validationErrors &&
                    <div className="rvp-header-card card alert alert-danger">
                        <div className="rvp-header-card-text container">
                            There was an issue getting user information, please contact support.
                        </div>
                    </div>
                }
                {minorAthleteTrainingViewModel?.isError === false && !validationErrors &&
                    <>
                        <form id="minor-athlete-training-form" onSubmit={handleSubmit} onKeyDown={handleKeyDown} ref={handleFormRefChange}>
                            <div asp-validation-summary="All" className="text-danger"></div>

                            <div className="form-group">
                                <div className="text-end mat-user-info-container">
                                    {minorAthleteTrainingViewModel && <label className="mat-user-info-label">{minorAthleteTrainingViewModel?.fullName} ({minorAthleteTrainingViewModel?.nationalId})</label>}
                                </div>
                                <br />
                                <div className="form-group">
                                    <div>
                                        <div className="text-start">
                                            I, the undersigned, ­­certify that I am the parent or legal guardian of the below named Minor Athlete.  I understand that my permission and authorization is required in order for Minor Athlete to be trained in abuse prevention by taking the SafeSport’s Core Center for SafeSport Training (the “Training”).  I also understand that Minor Athlete will not be permitted to register or take the Training unless I provide my permission by signing this Authorization.
                                            In connection with and in consideration of Minor Athlete’s participation in the Training, I, on behalf of Minor Athlete and myself, my heirs, personal representative(s) and assign(s) hereby represent and agree as follows:
                                        </div>
                                        <br />
                                        <div className="text-start">
                                            <ul>
                                                <li>I agree to allow the Minor Athlete to view and participate in the Training;</li>
                                                <li>I agree that I have had the opportunity to review the applicable terms, conditions, and/or policies (including, but not limited to, any applicable privacy policy), which are always available upon my request, and I agree to abide by them;</li>
                                                <li>I authorize the release and disclosure of Minor Athlete’s name, training date, national governing body, and other relevant information, as needed, to authorized third parties (e.g., the U.S. Olympic Committee, the U.S. Center for SafeSport, etc.) for the purposes of demonstrating that Minor Athlete has completed the Training:</li>
                                                <li>I agree to release the United States Bowling Congress and their respective officers, directors, employees, agents and volunteers from any and all liabilities, damages, losses and/or causes of action (collectively, “Claims”) that I may or Minor Athlete may suffer or have, including without limitation, to our persons or property or both, which arise out of, are related to or in connection with, or occur during Minor Athlete’s participation in the Training; and</li>
                                                <li>I agree that this Authorization shall be governed by the laws of the state of Wisconsin without giving effect to any choice or conflict of law principles, and if any portion of this Authorization is held invalid, the remainder of the Authorization shall continue in full force and effect;</li>
                                            </ul>
                                        </div>
                                        <div>
                                            The U.S. Bowling Congress is committed to supporting an environment in which all participants in the bowling community, be they athletes, coaches, administrators or volunteers, play an active role in creating an environment free of emotional, physical or sexual abuse, and we encourage you to take advantage of this opportunity to increase your awareness of these important issues.
                                        </div>
                                        <br />
                                        <div>
                                            <label className="fw-bold">I certify that I am 18 years of age or older and the parent or legal guardian of the Minor Athlete. In addition, I certify that I have read, fully understand, and agree to the terms of this Authorization, and I sign it voluntarily and with the full knowledge of its significance.</label>
                                            <br />
                                        </div>
                                        <br />
                                        <div className="text-start fw-bold">
                                            <label>Signing this form electronically, I acknowledge that it shall have the same validity and effect as if I signed this consent by hand.</label>
                                            <br />
                                        </div>
                                        <br />
                                        <div className="d-flex flex-wrap gap-2 justify-content-around">
                                            <div className={`MinorNationalId-container col-lg-5 col-12 form-field`}>
                                                <label htmlFor="MinorNationalId" className="form-label fw-bold required">Minor National Id</label>
                                                <ValidateInput errors={errors}>
                                                    <input type="text" className="form-control" id="MinorNationalId" name="minorNationalId" value={values?.minorNationalId || ''}
                                                        onChange={handleChange}
                                                        maxLength={19}
                                                        readOnly={true}
                                                        disabled={true} />
                                                </ValidateInput>
                                            </div>
                                            <div className={`MinorName-container col-lg-5 col-12 form-field`}>
                                                <label htmlFor="MinorName" className="form-label fw-bold required">Minor Name</label>
                                                <ValidateInput errors={errors}>
                                                    <input type="text" className="form-control" id="MinorName" name="minorName" value={values?.minorName || ''}
                                                        onChange={handleChange}
                                                        maxLength={19}
                                                        readOnly={true}
                                                        disabled={true} />
                                                </ValidateInput>
                                            </div>
                                            <div className={`ParentName-container col-lg-5 col-12 fw-bold form-field`}>
                                                <label htmlFor="ParentName" className="form-label required">Parent or Legal Guardian Name</label>
                                                <ValidateInput errors={errors}>
                                                    <input type="text" className="form-control" id="ParentName" name="parentName" value={values?.parentName || ''}
                                                        onChange={handleChange}
                                                        maxLength={19}
                                                        readOnly={submitMinorAthleteTrainingLoading}
                                                        disabled={submitMinorAthleteTrainingLoading} />
                                                </ValidateInput>
                                            </div>
                                            <div className={`ParentEmail-container col-lg-5 col-12 fw-bold form-field`}>
                                                <label htmlFor="ParentEmail" className="form-label required">Parent or Legal Guardian Email Address</label>
                                                <ValidateInput errors={errors}>
                                                    <input type="email" className="form-control" id="ParentEmail" name="parentEmail" value={values?.parentEmail || ''}
                                                        onChange={handleChange}
                                                        readOnly={submitMinorAthleteTrainingLoading}
                                                        disabled={submitMinorAthleteTrainingLoading}
                                                    />
                                                </ValidateInput>
                                            </div>
                                            <div className={`ParentSignature-container col-lg-5 col-12 fw-bold form-field`}>
                                                <label htmlFor="ParentSignature" className="form-label required">Parent or Legal Guardian Signature</label>
                                                <ValidateInput errors={errors}>
                                                    <input type="text" className="form-control" id="ParentSignature" name="parentSignature" value={values?.parentSignature || ''}
                                                        onChange={handleChange}
                                                        maxLength={19}
                                                        readOnly={submitMinorAthleteTrainingLoading}
                                                        disabled={submitMinorAthleteTrainingLoading} />
                                                </ValidateInput>
                                            </div>
                                            <div className={`parentPhoneField-container col-lg-5 col-12 fw-bold form-field`}>
                                                <label htmlFor="parentPhoneField" className="form-label required">Parent or Legal Guardian Phone Number</label>
                                                <ValidateInput errors={errors}>
                                                    <input type="text" className="form-control" id="parentPhoneField" name="parentPhone" value={values?.parentPhone || ''}
                                                        placeholder={values?.country === "US" || values?.country === "CA" ? "(999)999-9999 x9999" : ""}
                                                        onChange={handleChange}
                                                        maxLength={19}
                                                        readOnly={submitMinorAthleteTrainingLoading}
                                                        disabled={submitMinorAthleteTrainingLoading} />
                                                </ValidateInput>
                                            </div>

                                        </div>

                                        <br />
                                        <div className=" text-start">
                                            <label>If you have any questions, please contact <a href="mailto:RegisteredVolunteers@bowl.com?subject=Registered%20Volunteer%20Question" target="_blank">RVP@bowl.com</a>.</label>
                                            <br />
                                        </div>
                                        <div className=" text-start">
                                            <strong>Please be advised that once athletes turn 18, they will be required to take this training before continuing their participation in USBC sporting events,</strong> and so we encourage all 17-year olds to avoid any participation interruption and get ahead of the game by submitting valid parental consent and taking the training at their earliest opportunity.
                                            <br />
                                        </div>
                                        <br />
                                        <div className=" text-start">
                                            <div className={`DateOfSignature-container col-lg-3 col-12 form-field`}>
                                                <label htmlFor="DateOfSignature" className="form-label fw-bold required">Date of Signature</label>
                                                <ValidateInput errors={errors}>
                                                    <input type="date" className="form-control" id="DateOfSignature" name="dateOfSignature" value={formatDate(values?.dateOfSignature || '')}
                                                        onChange={handleChange}
                                                        readOnly={submitMinorAthleteTrainingLoading}
                                                        disabled={submitMinorAthleteTrainingLoading} />
                                                </ValidateInput>
                                            </div>
                                            <br />
                                        </div>
                                    </div>
                                    <LoadingButton id="btnSubmit" type="submit" className="btn btn-warning minor-form-loading-button"
                                        form="minor-athlete-training-form" loadingText='Approval Minor Athlete Training' loading={submitMinorAthleteTrainingLoading}>
                                        Approval Minor Athlete Training
                                    </LoadingButton>
                                    <span id="showLoading"><img src="" /></span>
                                </div>
                            </div>
                        </form>
                    </>
                }
            </LoadingComponent >
        </>
    );
}

export default MinorAthleteTraining;